<template>
  <ShipmentMap
    :id="mapId"
    :shipment="trackingSummary.shipments[0]"
    :events="trackingSummary.shipments[0]?.events ?? []"
    :static-map="staticMapUrl.data"
    :access-token="accessToken"
    :loading="staticMapUrl.loading"
    :dynamic="showDynamicMap"
    :interactive="isMapActionsEnabled"
  />
  <Content
    class="unauthenticated-track-and-trace"
    header-dark-bg
    :loading="loading"
  >
    <div
      v-for="shipment in trackingSummary.shipments"
      :key="shipment.id"
      class="shipment-container"
    >
      <ShipmentTrackingSection
        :shipment="shipment"
        :domain-branding="trackingSummary.domainBranding"
        :contact="trackingSummary.contact"
      />
      <ShipmentProgressSection
        class="tracking-header"
        :events="shipment.events"
        :service-incidents="shipment.incidents"
      />
    </div>
  </Content>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { PropType, Ref, ref, toRef, toRefs, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { UUID } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useMap } from '@/composables';
import { OrderTrackingSummary } from '@/model';

import ShipmentMap from '@/components/shipment/ShipmentMap.vue';
import ShipmentProgressSection from '@/components/shipment/tracking/ShipmentProgressSection.vue';
import ShipmentTrackingSection from '@/components/shipment/tracking/ShipmentTrackingSection.vue';
import Content from '@rygen/rygen-ui/components/content/Content.vue';

const props = defineProps({
  orderId: {
    type: Number,
    required: true,
  },
  accessToken: {
    type: String as PropType<UUID>,
    default: () => '',
  },
});
const { orderId, accessToken } = toRefs(props);

const { t } = useI18n();
const toast = useToast();

const trackingSummary: Ref<OrderTrackingSummary> = ref(
  new OrderTrackingSummary(),
);
const { isMapActionsEnabled, mapId, showDynamicMap, staticMapUrl } = useMap(
  toRef(() => trackingSummary.value.shipments[0]),
);

const loading = ref(false);

const getTrackingSummary = async () => {
  try {
    loading.value = true;
    trackingSummary.value =
      await api.orderTrackingLinkUnauthenticated.getOrderTrackingSummary(
        orderId.value,
        accessToken.value,
      );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    toast.addError({
      detail: t('errors.loadingParam', {
        param: t('orders.trackingInformation'),
      }),
      error: e,
    });
  } finally {
    loading.value = false;
  }
};

watch(
  orderId,
  async () => {
    await getTrackingSummary();
  },
  { immediate: true },
);
</script>

<style>
.unauthenticated-track-and-trace {
  --content-full-width: 900px;
}
</style>
